import { useSuspenseQuery } from '@apollo/client';
import { Suspense } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useParams } from '@xing-com/crate-core-router';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { UserBags } from '@xing-com/crate-people-directory-user-bags';
import { UserChunk } from '@xing-com/crate-people-directory-user-chunk';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';
import { Column, Grid, Row } from '@xing-com/xing-grid';

import { SPECIAL_CHARACTER_ID, SEO_URL_META_DATA } from './constants';
import { LetterPageByUsernameDocument } from './graphql/letter-page-by-username.query.gql-types';
import { ContentRow, StyledHeadline } from './letter-page-by-username.styles';
import { PageNamesTypes } from './types';
import type { PageName } from './types';

type BasicPageLayoutProps = {
  seoTitle: string;
  seoDescription: string;
  index: boolean;
  seoUrl: string;
  pageName: string;
  letter: string;
  pageHeaderAsSecondaryHeadline: boolean;
};

export const LetterPageByUsernameInner: React.FC<
  BasicPageLayoutProps & {
    headlineMessage: string;
  }
> = ({ headlineMessage, ...basicPageLayoutProps }) => {
  const { slugOrLetter: letter } = useParams();

  const { data, error } = useSuspenseQuery(LetterPageByUsernameDocument, {
    variables: { letter },
    skip: !letter,
  });
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  const { names, popularNames, breadcrumbs } = data?.peopleTopLevelPage || {};

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  const pageLayoutProps = {
    ...basicPageLayoutProps,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  if (error || !data) throw error;

  if (!data?.peopleTopLevelPage || !names || !names.length) {
    return (
      <EmptyStateFallback
        {...basicPageLayoutProps}
        headlineMessage={headlineMessage}
      />
    );
  }

  const withUserBags = popularNames && popularNames.length;

  const sanitizedNames =
    names
      ?.map((name): PageName | null => {
        const nameUrl = name?.url ? `${name.url}` : '';

        switch (name?.__typename) {
          case 'PeopleChildItem': {
            return {
              __typename: PageNamesTypes.child,
              name: name?.name || '',
              toName: name?.toName || '',
              url: nameUrl,
            };
          }
          case 'PeopleUserBagItem': {
            return {
              __typename: PageNamesTypes.userBag,
              name: name?.name || '',
              count: name?.count || 0,
              url: nameUrl,
            };
          }
          case 'PeopleProfileItem': {
            return {
              __typename: PageNamesTypes.profile,
              name: name?.name || '',
              photoUrl: name?.photoUrl || '',
              coach: name?.coach || false,
              url: nameUrl,
              occupationTitle: name?.occupationTitle || '',
              city: name?.city || '',
            };
          }
          default: {
            return null;
          }
        }
      })
      .reduce<Array<PageName>>((result, name: PageName | null) => {
        if (name) {
          return [...result, name];
        } else {
          return result;
        }
      }, []) || [];

  const sanitizedPopularNames = popularNames?.map((popularName) => {
    const popularNameUrl = popularName?.url ? `${popularName.url}` : '';

    return {
      name: popularName?.name || '',
      url: popularNameUrl,
      count: popularName?.count || 0,
    };
  });

  return (
    <PageLayout {...pageLayoutProps}>
      <Grid>
        {isDesktop ? <HeadlineRow headlineMessage={headlineMessage} /> : null}
        <ContentRow $isLoading={false}>
          <Column size={12} sizeWide={withUserBags ? 8 : 12}>
            {!isDesktop ? (
              <StyledHeadline size="large" forwardedAs="h1">
                {headlineMessage}
              </StyledHeadline>
            ) : null}
            {names ? (
              <UserChunk
                names={sanitizedNames}
                fullWidthLayout={!withUserBags}
              />
            ) : null}
          </Column>
          {withUserBags && sanitizedPopularNames ? (
            <Column size={12} sizeWide={4}>
              <UserBags
                activeLetter={letter}
                popularNames={sanitizedPopularNames}
              />
            </Column>
          ) : null}
        </ContentRow>
      </Grid>
    </PageLayout>
  );
};

const HeadlineRow: React.FC<{ headlineMessage: string }> = ({
  headlineMessage,
}) => {
  return (
    <Row>
      <Column sizeWide={12} size={0}>
        <StyledHeadline size="xxlarge" forwardedAs="h1">
          {headlineMessage}
        </StyledHeadline>
      </Column>
    </Row>
  );
};

const LoadingStateFallback: React.FC<
  BasicPageLayoutProps & {
    headlineMessage: string;
  }
> = ({ headlineMessage, ...basicPageLayoutProps }) => {
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  return (
    <PageLayout {...basicPageLayoutProps}>
      <Grid>
        <ContentRow $isLoading={true}>
          <Column size={12}>
            <StyledHeadline
              size={isDesktop ? 'xxlarge' : 'large'}
              forwardedAs="h1"
            >
              {headlineMessage}
            </StyledHeadline>
          </Column>
        </ContentRow>
      </Grid>
    </PageLayout>
  );
};

const EmptyStateFallback: React.FC<
  BasicPageLayoutProps & {
    headlineMessage: string;
  }
> = ({ headlineMessage, ...basicPageLayoutProps }) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <HeadlineRow headlineMessage={headlineMessage} />
      <UserMessage type={UserMessageType.empty} />
    </PageLayout>
  );
};

const ErrorFallback: React.FC<
  BasicPageLayoutProps & {
    headlineMessage: string;
  }
> = ({ headlineMessage, ...basicPageLayoutProps }) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <HeadlineRow headlineMessage={headlineMessage} />
      <UserMessage type={UserMessageType.error} />
    </PageLayout>
  );
};

export const LetterPageByUsername: React.FC = () => {
  const { slugOrLetter: letter } = useParams();
  const { formatMessage } = useIntl();
  const isSpecialCharacter = letter === SPECIAL_CHARACTER_ID;
  const headlineMessage = formatMessage(
    {
      id: isSpecialCharacter
        ? 'PD_200_LASTNAMES_SPECIAL'
        : 'PD_200_LASTNAMES_STARTING',
    },
    { letter_variable: letter.toUpperCase() }
  );
  const basicPageLayoutProps = {
    letter,
    pageHeaderAsSecondaryHeadline: true,
    seoTitle: formatMessage(
      { id: 'PD_LETTER_ROOT_TITLE' },
      { display_name: headlineMessage }
    ),
    index: true,
    seoDescription: formatMessage({ id: 'PD_META_DESCRIPTION_LVL_0' }),
    seoUrl: `${SEO_URL_META_DATA}${letter}`,
    pageName: `${letter}`,
  };

  return (
    <ErrorBoundary
      onError={() => (
        <ErrorFallback
          {...basicPageLayoutProps}
          headlineMessage={headlineMessage}
        />
      )}
    >
      <Suspense
        fallback={
          <LoadingStateFallback
            {...basicPageLayoutProps}
            headlineMessage={headlineMessage}
          />
        }
      >
        <LetterPageByUsernameInner
          {...basicPageLayoutProps}
          headlineMessage={headlineMessage}
        />
      </Suspense>
    </ErrorBoundary>
  );
};
